import { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import ConfirmationPopup from "../common/ConfirmationPopup";
import { hostName } from "../../config/config";

const AdditionalActionItems = (props) => {
  const {
    isView,
    actionEntries,
    setActionEntries,
    actionFieldsRef,
    onDeleteImage,
    onUploadImage,
    reset,
    isApprover,
    isRequestForChange,
    isAppprovalPending,
    handleApproverComments,
    handleApproverCheckbox,
    approverComments,
    approverCheckbox,
    mandatoryFieldRef,
  } = props;

  const [showCamera, setShowCamera] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const webcamRef = useRef(null);

  const [showConfirmationPopup, setShowConfirmationPopup] = useState({
    show: false,
    entryIndex: 0,
    imgIndex: 0,
  });

  const ImageProxyBaseUrl = hostName + "/api/Inspection/api/image?blobUrl=";

  const videoConstraints = {
    width: 640,
    height: 480,
    facingMode: facingMode,
  };

  useEffect(() => {
    if (reset) {
      setActionEntries((prev) =>
        prev.map((item) => ({
          ...item, // keep stable fields like id if needed
          actionLabel: "",
          actionValue: 0,
          actionSeverity: "Select",
          actionCategory: "Select",
          actionDueDate: "",
          actionRemediation: "",
          actionFile: [],
        }))
      );

      actionEntries.map((entry, i) => {
        resetIndvidualField("actionLabel" + i);
        resetIndvidualField("actionValue" + i);
        resetIndvidualField("actionSeverity" + i);
        resetIndvidualField("actionCategory" + i);
        resetIndvidualField("actionDueDate" + i);
        resetIndvidualField("actionRemediation" + i);
      });
    }
  }, [reset]);

  useEffect(() => {
    if(actionEntries && actionEntries.length > 0){
      setActionEntries((prev) =>
        prev.map((item, index) => ({
          ...item, // keep stable fields like id if needed
          hideRemediation:
            actionEntries[index]?.actionCategory === "5" ? true : false,
        }))
      );
    }
  })

  const addEntry = () => {
    const newId =
      actionEntries.length > 0
        ? Math.max(...actionEntries.map((e) => e.id)) + 1
        : 1;
    setActionEntries([
      ...actionEntries,
      {
        id: newId,
        actionLabel: "",
        actionValue: null,
        actionSeverity: "Select",
        actionCategory: "Select",
        actionDueDate: "",
        actionRemediation: "",
        hideRemediation: false,
        actionFile: [],
      },
    ]);
  };

  const removeEntry = (id) => {
    setActionEntries(actionEntries?.filter((entry) => entry.id !== id));
  };

  const switchCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const closeCamera = () => {
    setShowCamera(false);
  };

  const closePopup = () => {
    setShowConfirmationPopup({
      show: false,
      entryIndex: 0,
      imgIndex: 0,
    });
  };

  const getSeverityDate = (sev) => {
    switch (sev) {
      case "1":
        return new Date().toISOString().split("T")[0];
      case "2":
        return new Date(new Date().setDate(new Date().getDate() + 30))
          .toISOString()
          .split("T")[0];
      case "3":
        return new Date(new Date().setDate(new Date().getDate() + 90))
          .toISOString()
          .split("T")[0];
      case "4":
        return new Date(new Date().setDate(new Date().getDate() + 180))
          .toISOString()
          .split("T")[0];
      case "Select":
        return "";
      default:
        return new Date().toISOString().split("T")[0];
    }
  };

  const handleActionDataChange = (field, value, index) => {
    if (field === "actionSeverity") {
      const severityDueDate = getSeverityDate(value);
      setActionEntries((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                [field]: value,
                actionDueDate: severityDueDate,
              }
            : item
        )
      );
      resetIndvidualField("actionDueDate" + index);
    } else if (field === "actionCategory") {
      if (value === "5") {
        setActionEntries((prev) =>
          prev.map((item, i) =>
            i === index
              ? {
                  ...item,
                  [field]: value,
                  actionRemediation:
                    "Remediation - Fire Door Replacement required",
                  hideRemediation: true,
                }
              : item
          )
        );
      } else {
        setActionEntries((prev) =>
          prev.map((item, i) => {
            if (i !== index) return item;
            const shouldClear =
              item.actionRemediation ===
              "Remediation - Fire Door Replacement required";
            return {
              ...item,
              [field]: value,
              actionRemediation: shouldClear ? "" : item.actionRemediation,
              hideRemediation: false,
            };
          })
        );
      }
    } else {
      setActionEntries((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                [field]: value,
              }
            : item
        )
      );
    }

    resetIndvidualField(field + index);
  };

  const handleFileChange = (event, index) => {
    const file = event.target.files?.[0] ?? null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newImages = [reader.result];
        onUploadImage(newImages, index);
      };
      reader.readAsDataURL(file);
    }
  };

  const capturePhoto = (index) => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      const newImages = [imageSrc];
      setActionEntries((prev) =>
        prev.map((item, i) => {
          if (i !== index) return item;

          const prevFieldValue = item.actionFile; // e.g., previous additionalDocsArr

          const updatedFile = [...prevFieldValue, ...newImages];

          return { ...item, actionFile: updatedFile };
        })
      );
      setShowCamera(false);
    }
  };

  const triggerFileInput = (index) => {
    actionFieldsRef.current["actionFile" + index].click();
  };

  const handleReset = (index) => {
    setActionEntries((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              actionLabel: "",
              actionValue: 0,
              actionSeverity: "Select",
              actionCategory: "Select",
              actionDueDate: "",
              actionRemediation: "",
              actionFile: [],
            }
          : item
      )
    );
    resetIndvidualField("actionLabel" + index);
    resetIndvidualField("actionValue" + index);
    resetIndvidualField("actionSeverity" + index);
    resetIndvidualField("actionCategory" + index);
    resetIndvidualField("actionDueDate" + index);
    resetIndvidualField("actionRemediation" + index);
  };

  const resetIndvidualField = (field) => {
    actionFieldsRef.current[field] != null
      ? (actionFieldsRef.current[field].style.border = "1px solid #dee2e6")
      : null;
  };

  const removeImage = (entryIndex, imgIndex) => {
    const imgArr = actionEntries[entryIndex].actionFile;
    const imageToDelete = imgArr[imgIndex];
    setActionEntries((prev) =>
      prev.map((item, i) => {
        if (i !== entryIndex) return item;

        const newImages = item.actionFile.filter(
          (_, index) => index !== imgIndex
        );

        return { ...item, actionFile: newImages };
      })
    );
    onDeleteImage(imageToDelete);

    if (actionFieldsRef.current["actionFile" + entryIndex]) {
      actionFieldsRef.current["actionFile" + entryIndex].value = "";
    }
    setShowConfirmationPopup({
      show: false,
      entryIndex: 0,
      imgIndex: 0,
    });
  };

  return (
    <div className="card-body">
      <ConfirmationPopup
        show={showConfirmationPopup.show}
        headingText="Delete Image"
        infoText="Please confirm if you want to delete the selected image"
        cancelText="Cancel"
        submitText="Delete"
        onCancel={() => {
          closePopup();
        }}
        onSubmit={() => {
          removeImage(
            showConfirmationPopup.entryIndex,
            showConfirmationPopup.imgIndex
          );
        }}
      />
      <div className="col-sm-12">
        <p className="fs-6 fw-semibold">Additional Action Items</p>
      </div>
      {!isView && (
        <div className="col-sm-12 d-flex align-items-center justify-content-center gap-2 mb-3">
          <p className="fs-8 fw-light mb-0">
            Add any more action items if required
          </p>
          <button
            type="button"
            title="Add item"
            className="btn btn-dark"
            onClick={addEntry}
          >
            <i className="fa fa-solid fa-plus"></i>
          </button>
        </div>
      )}
      {actionEntries?.length > 0 &&
        actionEntries?.map((entry, index) => {
          const isRequstForChangeDisabled = isApprover
            ? true
            : isView && !approverCheckbox[entry.actionLabel];
          return (
            <div key={index} className="col-sm-12 mb-3 border p-2 rounded">
              {isRequestForChange && isApprover && (
                <div className="form-floating d-flex justify-content-end pb-2">
                  <input
                    className="form-check-input form-check-input-checkbox custom-checkbox"
                    type="checkbox"
                    id="headCheckbox"
                    disabled={!isAppprovalPending}
                    name={entry.actionLabel}
                    checked={approverCheckbox?.[entry.actionLabel] || false}
                    onChange={(e) => handleApproverCheckbox(e)}
                  />
                </div>
              )}
              <div className="form-floating mb-2">
                <input
                  type="text"
                  className="form-control"
                  id="floatingInputValue"
                  value={entry.actionLabel}
                  disabled={isRequstForChangeDisabled}
                  ref={(el) =>
                    (actionFieldsRef.current["actionLabel" + index] = el)
                  }
                  name="actionLabel"
                  onChange={(e) => {
                    handleActionDataChange(
                      "actionLabel",
                      e.target.value,
                      index
                    );
                  }}
                />
                <label htmlFor="floatingInputValue">
                  Label<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div className="form-floating mb-2">
                <input
                  type="number"
                  pattern="[0-9]*"
                  inputMode="decimal"
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault(); // Prevent invalid characters
                    }
                  }}
                  disabled={isRequstForChangeDisabled}
                  className="form-control"
                  id="floatingInputValue"
                  value={entry.actionValue}
                  min={0}
                  ref={(el) =>
                    (actionFieldsRef.current["actionValue" + index] = el)
                  }
                  name="actionValue"
                  onChange={(e) => {
                    handleActionDataChange(
                      "actionValue",
                      e.target.value,
                      index
                    );
                  }}
                />
                <label htmlFor="floatingInputValue">
                  Value(mm)<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div className="form-floating flex-grow-1 mb-2">
                <select
                  className="form-select"
                  id="floatingSelect"
                  disabled={isRequstForChangeDisabled}
                  aria-label="Floating label select example"
                  value={entry.actionSeverity}
                  ref={(el) =>
                    (actionFieldsRef.current["actionSeverity" + index] = el)
                  }
                  name="actionSeverity"
                  onChange={(e) => {
                    handleActionDataChange(
                      "actionSeverity",
                      e.target.value,
                      index
                    );
                  }}
                >
                  <option selected>Select</option>
                  <option value="1">Critical</option>
                  <option value="2">High</option>
                  <option value="3">Medium</option>
                  <option value="4">Low</option>
                </select>
                <label htmlFor="floatingSelect">
                  Severity<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div className="form-floating flex-grow-1 mb-2">
                <select
                  className="form-select"
                  id="floatingSelect"
                  disabled={isRequstForChangeDisabled}
                  aria-label="Floating label select example"
                  value={entry.actionCategory}
                  ref={(el) =>
                    (actionFieldsRef.current["actionCategory" + index] = el)
                  }
                  name="actionCategory"
                  onChange={(e) => {
                    handleActionDataChange(
                      "actionCategory",
                      e.target.value,
                      index
                    );
                  }}
                >
                  <option selected>Select</option>
                  <option value="1">Firedoor Repair</option>
                  <option value="2">Signage repair</option>
                  <option value="3">Fire door Replacement</option>
                  <option value="4">Testing, Records, Log Book</option>
                  <option value="5">Door Replacement required</option>
                  <option value="6">Door Repair</option>
                </select>
                <label htmlFor="floatingSelect">
                  Category<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div className="form-floating flex-grow-1 mb-2">
                <input
                  type="date"
                  className="form-control"
                  id="duedate"
                  disabled
                  value={entry.actionDueDate}
                  ref={(el) =>
                    (actionFieldsRef.current["actionDueDate" + index] = el)
                  }
                  name="actionDueDate"
                />
                <label htmlFor="duedate">
                  Due Date<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              {!entry.hideRemediation && (
                <div className="d-flex gap-3 flex-wrap mb-2">
                  <div className="form-floating flex-grow-1">
                    <textarea
                      rows={4}
                      type="text"
                      className="form-control"
                      id="remediation"
                      style={{ minHeight: "auto", height: "auto" }}
                      disabled={isRequstForChangeDisabled}
                      placeholder="Remedial/Action required"
                      value={entry.actionRemediation}
                      ref={(el) =>
                        (actionFieldsRef.current["actionRemediation" + index] =
                          el)
                      }
                      name="actionRemediation"
                      onChange={(e) => {
                        handleActionDataChange(
                          "actionRemediation",
                          e.target.value,
                          index
                        );
                      }}
                    />
                    <label htmlFor="remediation">
                      Remedial/Action required
                      <span style={{ color: "red" }}>*</span>
                    </label>
                  </div>
                </div>
              )}
              {isRequestForChange && (
                <div
                  className="form-floating mb-1"
                  style={
                    approverCheckbox?.[entry.actionLabel]
                      ? {}
                      : { display: "none" }
                  }
                >
                  <input
                    type="text"
                    disabled={!isAppprovalPending}
                    ref={(el) =>
                      (mandatoryFieldRef.current[
                        entry.actionLabel + "ApproverComments"
                      ] = el)
                    }
                    className="form-control"
                    id="floatingInputValue"
                    name={entry.actionLabel}
                    value={approverComments?.[entry.actionLabel]}
                    onChange={(e) => {
                      handleApproverComments(e);
                    }}
                  />
                  <label htmlFor="floatingInputValue">
                    Approver Comments for {entry.actionLabel}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                </div>
              )}
              {entry.actionFile?.length === 0 && (
                <p className="image-mandatory">
                  * Please upload at least one image
                </p>
              )}
              <div className="d-flex justify-content-end align-items-center gap-3 flex-wrap flex-sm-nowrap pt-2 pb-3">
                {!showCamera && !isRequstForChangeDisabled && (
                  <input
                    type="file"
                    accept=".jpg, .jpeg, .png, .gif"
                    onChange={(e) => handleFileChange(e, index)}
                    className="form-control"
                    id="chooseFiles"
                    style={{ display: "none" }}
                    ref={(el) =>
                      (actionFieldsRef.current["actionFile" + index] = el)
                    }
                  />
                )}
                {!showCamera && !isRequstForChangeDisabled && (
                  <i
                    className="fa-solid fa-upload fa-xl"
                    title="Upload Photo"
                    style={{ marginTop: "3px", cursor: "pointer" }}
                    onClick={() => triggerFileInput(index)}
                  ></i>
                )}
                {!showCamera && !isRequstForChangeDisabled && (
                  <i
                    className="fa-solid fa-camera fa-xl"
                    onClick={() => setShowCamera(true)}
                    title="Open Camera"
                    style={{ marginTop: "3px", cursor: "pointer" }}
                  ></i>
                )}
                {!isView && (
                  <i
                    className="fa-solid fa-arrows-rotate fa-xl"
                    onClick={() => handleReset(index)}
                    title="Reset"
                    style={{ marginTop: "3px", cursor: "pointer" }}
                  ></i>
                )}
                {!isView && (
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{
                      height: "24px",
                      width: "24px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    title="Remove Item"
                    onClick={() => removeEntry(entry.id)}
                    //   disabled={entries.length === 1 ? true : false}
                  >
                    <i className="fa-solid fa-minus"></i>
                  </button>
                )}
              </div>
              {showCamera && (
                <div>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    height={280}
                    screenshotFormat="image/jpeg"
                    width={290}
                    videoConstraints={videoConstraints}
                  />
                  <div
                    className="d-flex flex-wrap gap-3 pb-2"
                    style={{ justifyContent: "center" }}
                  >
                    <i
                      className="fa-solid fa-camera fa-xl"
                      onClick={() => capturePhoto(index)}
                      style={{ cursor: "pointer" }}
                    ></i>
                    <i
                      className="fa-solid fa-camera-rotate fa-xl"
                      onClick={switchCamera}
                      style={{ cursor: "pointer" }}
                    ></i>
                    <i
                      className="fa-solid fa-xmark fa-xl"
                      onClick={closeCamera}
                      style={{ cursor: "pointer" }}
                      title="Close Camera"
                    ></i>
                  </div>
                </div>
              )}

              {entry.actionFile?.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  {/* <h4>Captured Images</h4> */}
                  <div
                    style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                  >
                    {entry.actionFile.map((img, ind) => (
                      <div
                        key={ind}
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "200px",
                          border: "2px solid #000",
                        }}
                      >
                        <img
                          src={
                            img?.includes("data:image")
                              ? img
                              : ImageProxyBaseUrl + encodeURIComponent(img)
                          }
                          alt={`Captured ${ind + 1}`}
                          style={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                          }}
                        />

                        {!isRequstForChangeDisabled && (
                          <i
                            className="fa-solid fa-xmark"
                            onClick={() =>
                              setShowConfirmationPopup({
                                show: true,
                                entryIndex: index,
                                imgIndex: ind,
                              })
                            }
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              backgroundColor: "transparent",
                              color: "black",
                              border: "none",
                              borderRadius: "50%",
                              width: "24px",
                              height: "24px",
                              cursor: "pointer",
                            }}
                          ></i>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default AdditionalActionItems;
